import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroData, products, industries } from '../data/mock';
import { ArrowRight, CheckCircle, Snowflake, Thermometer, Shield, Clock } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroData.backgroundImage}
            alt="Commercial Refrigeration"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
            <Snowflake className="text-orange-500" size={20} />
            <span className="text-orange-500 text-sm font-semibold">Serving Florida Since 2023</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            {heroData.title}
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-3xl mx-auto">
            {heroData.subtitle}
          </p>
          
          <p className="text-base md:text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
            {heroData.description}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              asChild 
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-6 text-lg"
            >
              <Link to="/contact">
                {heroData.ctaPrimary}
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg"
              variant="outline"
              className="border-orange-500 text-orange-500 hover:bg-orange-500/10 px-8 py-6 text-lg"
            >
              <Link to="/products">{heroData.ctaSecondary}</Link>
            </Button>
          </div>
        </div>

        {/* Animated Snowflakes */}
        <div className="absolute inset-0 pointer-events-none">
          <Snowflake className="absolute top-20 left-20 text-orange-500/20 animate-pulse" size={40} />
          <Snowflake className="absolute bottom-40 right-32 text-orange-500/20 animate-pulse" size={30} />
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose Sub-Zero Coolers?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Built from determination and decades of expertise in commercial refrigeration
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Shield,
                title: "Premium Quality",
                description: "Built to last with commercial-grade materials and components"
              },
              {
                icon: Thermometer,
                title: "Precise Control",
                description: "Advanced temperature management for perfect preservation"
              },
              {
                icon: Clock,
                title: "20+ Years Experience",
                description: "Founded by Luis Granda with two decades of refrigeration expertise"
              },
              {
                icon: CheckCircle,
                title: "Licensed & Insured",
                description: "Fully certified and insured for your peace of mind and protection"
              }
            ].map((feature, index) => (
              <Card key={index} className="bg-gray-800/50 border-gray-700 hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/10 mb-4">
                    <feature.icon className="text-orange-500" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Featured Products
            </h2>
            <p className="text-gray-400 text-lg">
              Explore our range of commercial refrigeration solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {products.slice(0, 3).map((product) => (
              <Card key={product.id} className="bg-gray-800 border-gray-700 overflow-hidden hover:border-orange-500/50 transition-all duration-300 group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-orange-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                      {product.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-3">{product.name}</h3>
                  <p className="text-gray-400 mb-4">{product.description}</p>
                  <div className="space-y-2 mb-4">
                    {product.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm">
                        <CheckCircle size={16} className="text-orange-500" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600 text-black font-semibold">
              <Link to="/products">
                View All Products
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Industries We Serve
            </h2>
            <p className="text-gray-400 text-lg">
              Trusted refrigeration solutions across multiple sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <Card key={index} className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 hover:border-orange-500/50 transition-all duration-300 group cursor-pointer">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">
                    ❄️
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-orange-500 transition-colors">
                    {industry.name}
                  </h3>
                  <p className="text-gray-400 text-sm">{industry.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-black/80 text-lg mb-8">
            Contact us today for a free consultation and quote on your commercial refrigeration needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-black hover:bg-gray-900 text-orange-500 font-bold px-8">
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-black text-black hover:bg-black/10 font-bold px-8">
              <a href="tel:772-362-9384">Call 772-362-9384</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
